import { IClient, Typestate } from "@interfaces";
import { memo, useEffect, useLayoutEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Radio, Row, Col, Select, DatePicker, Space } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";
import { FormAction } from "@components";
import { PATH_CLIENT_INFO, geastral, enumReferred, region_towship, enumJoinedProgram } from "@configs";
import { setLoading } from "@redux";
import {
  getApiErrorMessage,
  useCreateClient,
  useNotify,
  useUpdateClient,
} from "@utils";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import dayjs from "dayjs";
import { SharedButton } from "@components";
import styled from "styled-components";

interface IProps {
  client?: IClient;
}
const dateFormat = "DD-MM-YYYY";
const initValue: IClient = {
  name: "",
  phoneNumber: "",
  state: "",
  township: "",
  dateOfBirth: dayjs().subtract(18, "year"),
  noOfChildren: 0,
  geastralMonth: 0,
  pragrancyStatus: true,
  referredBy: [],
  deliverySite: "",
  ANCareVisitStatus: false,
  age: dayjs().subtract(18, "year").year(),
  status: false,
  stateId: "",
};

export const ClientForm = memo(({ client }: IProps) => {
  const { YupClient } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const clientSchema = Yup.object().shape(YupClient);
  const [data, setData] = useState<IClient>(initValue);
  const [region, setRegion] = useState<string>("");
  const [clientForm] = Form.useForm();
  const isSubmitting = createClient.isPending || updateClient.isPending;

  useEffect(() => {
    dispatch(setLoading(isSubmitting));
  }, [dispatch, isSubmitting]);

  useEffect(() => {
    clientForm.setFieldsValue({ program: enumJoinedProgram.MNCH });
  }, [clientForm, data]);
  useEffect(() => {
    if (client) {
      setData({ ...data, ...client, dateOfBirth: dayjs(client.dateOfBirth) });
      clientForm.setFieldsValue({
        ...data,
        ...client,
        noOfChildren: Number(client?.noOfChildren),
        dateOfBirth: dayjs(client.dateOfBirth),
        age: dayjs(client?.dateOfBirth).year().toString(),

      });
      setRegion(client?.state || "");
    }
  }, [client]);

  const onFinish = async (values: IClient) => {
    try {
      if (client) {
        await updateClient.mutateAsync({
          payload: {
            ...data,
            ...values,
            noOfChildren: Number(values?.noOfChildren),
            dateOfBirth: dayjs(values.dateOfBirth),
            age: dayjs(client?.dateOfBirth).year().toString(),
            status: client?.status,
            deliverySite: client?.deliverySite,
          } as IClient,
          id: data?.userId || "",
        });
      } else {
        await createClient.mutateAsync({
          ...data,
          ...values,
          noOfChildren: Number(values?.noOfChildren),
          dateOfBirth: dayjs(values.dateOfBirth),
          age: dayjs(values?.dateOfBirth).year().toString(),
        } as IClient);
      }
      success(t("message.create.success"));
      history.push(PATH_CLIENT_INFO);
    } catch (err) {
      error(getApiErrorMessage(err, t("message.create.fail")));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await clientSchema.validateSyncAt(field, { [field]: value });
    },
  };
  const selectedState: Typestate = Form.useWatch("state", clientForm);
  const selectedTownship: string = Form.useWatch("township", clientForm);
  const pragrancyStatus: boolean = Form.useWatch("pragrancyStatus", clientForm);
  const referredBy: enumReferred[] = Form.useWatch("referredBy", clientForm);
  const handleChangeState = (value: string) => {
    // clear township name while change state
    clientForm.setFieldsValue({ township: "" });
  };
  const isValid = () => !!clientForm.getFieldsError().filter(({ errors }) => errors.length).length;

  const goBack = () => history.goBack();

  const options = [
    {
      value: enumReferred.MMCW,
      label: enumReferred.MMCW,
    },
    {
      value: enumReferred.TELE_HEALTH,
      label: enumReferred.TELE_HEALTH,
    },
    {
      value: enumReferred.PACT,
      label: enumReferred.PACT,
    },
    {
      value: enumReferred.OTHERS,
      label: enumReferred.OTHERS,
    },
  ];
  const gestationalAgeOptions = ["Unknown", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

  const handleChangeReffer = (value: enumReferred[]) => {
    clientForm.setFieldValue("referredBy", value);
  };

  return (
    <Form
      form={clientForm}
      name="basic"
      initialValues={data}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.name")} name="name" rules={[yupSync]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.phone_number_require")} name="phoneNumber" rules={[yupSync]}>
            <Input type="number" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.state")} name="state" rules={[yupSync]}>
            <Select value={selectedState} onChange={handleChangeState}>
              {Object.keys(region_towship).map((item) => (
                <Select.Option value={item} key={item}>
                  {t(`page.${item}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.townshship")} name="township" rules={[yupSync]}>
            <Select value={selectedTownship}>
              {selectedState &&
                region_towship[selectedState.toLowerCase()].map((item) => (
                  <Select.Option value={item} key={item}>
                    {item}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.date_of_birth")} name="dateOfBirth" rules={[yupSync]}>
            <DatePicker format={dateFormat} />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.number_of_children_req")} name="noOfChildren" rules={[yupSync]}>
            <Input type="number" onWheel={(event) => event.currentTarget.blur()} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.pregnancy_status")}
            name="pragrancyStatus"
            valuePropName="value"
            // define valuePropName is what value will accept by radio group
          >
            <Radio.Group>
              <Space>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.gestational_age")} name="geastralMonth" rules={[yupSync]}>
            <Select disabled={!pragrancyStatus}>
              {geastral.map((month) => (
                <Select.Option value={month} key={month}>
                  {month}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.referredBy")} name="referredBy" rules={[yupSync]}>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="choose referred by"
              onChange={handleChangeReffer}
              value={referredBy}
              options={options}
              showSearch={false}
            />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.anc_visit_status")}
            name="ANCareVisitStatus"
            valuePropName="value"
            // define valuePropName is what value will accept by radio group
          >
            <Radio.Group>
              <Space>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.gestational_age_of_1_st_AN_care_visit")}
            name="gestational_age_of_1_st_AN_care_visit"
            rules={[yupSync]}
          >
            <Select placeholder={t("page.select")}>
              {gestationalAgeOptions.map((item) => (
                <Select.Option key={item} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.total_numbers_of_AN_care_visit")}
            name="total_numbers_of_AN_care_visit"
            rules={[yupSync]}
          >
            <Input type="number" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="program" hidden>
        <Input type="hidden" />
      </Form.Item>

      <Form.Item>
        <StyledFormAction>
          <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
            {t("page.cancel")}
          </SharedButton>
          <SharedButton type="primary" htmlType="submit" size="middle" disabled={!pragrancyStatus}>
            {t("page.create")}
          </SharedButton>
        </StyledFormAction>
      </Form.Item>
    </Form>
  );
});
const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
